import { Check, X } from 'lucide-react';
import type { ComponentProps } from 'react';

import { PopoverContent, type PopoverContentProps } from '@vs-shop/ui/popover';
import { clsxm } from '@vs-shop/utils';

/**
 * The comparison notice: what a storefront says the moment a product joins the
 * comparison set — that it worked, where to see it, and how to make the message
 * go away.
 *
 *   <ProductComparisonNoticePopup align="center">
 *     <ProductComparisonNotice>
 *       <ProductComparisonNoticeMark />
 *       <ProductComparisonNoticeText>
 *         Produkt přidán do porovnání. <a href={compareUrl}>Zobrazit porovnání</a>
 *       </ProductComparisonNoticeText>
 *       <ProductComparisonNoticeClose onClick={close} aria-label={closeLabel} />
 *     </ProductComparisonNotice>
 *   </ProductComparisonNoticePopup>
 *
 * Two placements already: the product panel's compare tool anchors it under the
 * tool row, and a product card's compare control anchors it to the card's
 * corner. Both say the same thing in the same box — only where it points
 * differs, which is the popup's `align` and nothing else.
 *
 * The sentence and the way through are the tenant's: the copy is children, and
 * so is the link, because where a comparison lives is a route this package does
 * not know. `@vs-shop/ui`'s popover owns the anchoring, the outside-click
 * dismissal and the focus handling — see `productComparisonNoticePopup.tsx`.
 */

/**
 * The surface the notice hangs on.
 *
 * Above the control that caused it, always: the notice reports on something the
 * shopper just clicked, and a message that appeared BELOW the click would be
 * under the pointer that made it. Which edge it lines up with is the placement's
 * — a tool row anchors it centred, a card's corner control pulls it to the end —
 * so `align` stays a caller's prop while `side` does not.
 *
 * It draws its own box over the primitive's: narrower, more rounded, with a
 * quieter edge, and with the primitive's padding taken off, because the notice
 * inside it carries its own.
 *
 * `'use client'` is absent here on purpose. The interactive piece is the popover
 * primitive itself; this is a plain component that renders it, so a tenant's own
 * client composition holds the open state.
 */
const productComparisonNoticePopupClasses = 'border-foreground/25 max-w-64 rounded-lg p-0';

export type ProductComparisonNoticePopupProps = Omit<PopoverContentProps, 'side'>;

/** The popup the notice fills. */
export const ProductComparisonNoticePopup = ({
  className,
  ...props
}: ProductComparisonNoticePopupProps) => (
  <PopoverContent
    data-slot="product-comparison-notice-popup"
    side="top"
    className={clsxm(productComparisonNoticePopupClasses, className)}
    {...props}
  />
);

/**
 * The notice's box.
 *
 * Asymmetric padding rather than even: the trailing gutter is wider because the
 * way out hangs in that corner, over the text rather than after it, so a
 * sentence that wraps to two lines does not run under the control.
 */
const productComparisonNoticeClasses = 'relative flex gap-2 py-2 pr-6 pl-4';

export type ProductComparisonNoticeProps = ComponentProps<'div'>;

/** The notice. */
export const ProductComparisonNotice = ({ className, ...props }: ProductComparisonNoticeProps) => (
  <div
    data-slot="product-comparison-notice"
    className={clsxm(productComparisonNoticeClasses, className)}
    {...props}
  />
);

/**
 * The tick.
 *
 * The glyph is the part's own rather than a caller's: this notice exists to
 * report one outcome, and a call site free to pass its own mark could report a
 * failure in the colour of a success. It holds its width against a sentence
 * that wraps, and opens the notice.
 */
const productComparisonNoticeMarkClasses = 'text-success size-5 shrink-0';

export type ProductComparisonNoticeMarkProps = Omit<ComponentProps<'svg'>, 'children'>;

/** The notice's mark. */
export const ProductComparisonNoticeMark = ({
  className,
  ...props
}: ProductComparisonNoticeMarkProps) => (
  <Check
    data-slot="product-comparison-notice-mark"
    className={clsxm(productComparisonNoticeMarkClasses, className)}
    {...props}
  />
);

/**
 * What happened, and the way through to the comparison.
 *
 * One part rather than two, because the link is inside the sentence rather than
 * after it — which language puts it where is a translator's decision, so the
 * whole sentence including its anchor arrives as children.
 */
const productComparisonNoticeTextClasses = 'text-sm/5';

export type ProductComparisonNoticeTextProps = ComponentProps<'span'>;

/** The notice's sentence. */
export const ProductComparisonNoticeText = ({
  className,
  ...props
}: ProductComparisonNoticeTextProps) => (
  <span
    data-slot="product-comparison-notice-text"
    className={clsxm(productComparisonNoticeTextClasses, className)}
    {...props}
  />
);

/** The mark inside the way out. Internal anatomy: no composition renders it. */
const productComparisonNoticeCloseIconClasses = 'size-4 shrink-0';

/**
 * The way out, in the notice's trailing corner and over the text rather than
 * beside it, so dismissing costs the sentence no width.
 *
 * Its mark is the part's own, so the control cannot be composed without one;
 * what it is CALLED is the tenant's, through `aria-label` — a control whose
 * only content is a glyph reaches assistive tech unnamed otherwise.
 */
const productComparisonNoticeCloseClasses =
  'hover:bg-background-inverted/10 text-foreground/50 hover:text-foreground absolute top-0.5 right-0.5 rounded-full p-1 transition-colors';

export type ProductComparisonNoticeCloseProps = ComponentProps<'button'>;

/** The control that dismisses the notice. */
export const ProductComparisonNoticeClose = ({
  className,
  children,
  type,
  ...props
}: ProductComparisonNoticeCloseProps) => (
  <button
    data-slot="product-comparison-notice-close"
    type={type ?? 'button'}
    className={clsxm(productComparisonNoticeCloseClasses, className)}
    {...props}
  >
    <X
      data-slot="product-comparison-notice-close-icon"
      className={productComparisonNoticeCloseIconClasses}
    />
    {children}
  </button>
);
